import { type BaseResponse } from "@/models/responses/base/baseResponse.response";
import AuthService from "@/services/auth/auth.service";
import { useMutation } from "@tanstack/react-query";
import type { RegisterEmployerDto } from "@/models/dto/register.dto";
import type { User } from "@/models/responses/auth/user.response";
import type { InitiateAuthDto } from "@/models/dto/initiate-auth.dto";
import type { VerifyOTPDto } from "@/models/dto/verifyOTP.dto";
import type { CreateAuthorizedContactDto } from "@/models/dto/create-authorized-contact.dto";
import type { InitiateAuthData } from "@/models/responses/auth/initiateAuth.response";
import type { VerifyLoginOtpData } from "@/models/responses/auth/verifyLoginOtp.response";

export const useVerifyLoginOTP = () => {
  return useMutation<VerifyLoginOtpData, Error, { employerCode: string; otpCode: string }>({
    mutationFn: ({ employerCode, otpCode }) => {
      return AuthService.verifyLoginOTP({ employerCode, otpCode });
    },
  });
};

export const useLogin = () => {
  return useMutation<BaseResponse<User>, Error, string>({
    mutationFn: (employerCode) => AuthService.login({ employerCode }),
  });
};

export const useInitiateAuth = () => {
  return useMutation<BaseResponse<InitiateAuthData>, Error, InitiateAuthDto>({
    mutationFn: async (dto) => {
      const response = await AuthService.initiateAuth(dto);
      return response as unknown as BaseResponse<InitiateAuthData>;
    },
  });
};

export const useRegisterEmployer = () => {
  return useMutation<BaseResponse<unknown>, Error, RegisterEmployerDto>({
    mutationFn: (dto) => AuthService.registerEmployer(dto),
  });
};

export const useResendRegistrationOTPToEmployer = () => {
  return useMutation<BaseResponse<unknown>, Error, string>({
    mutationFn: (employerCode) =>
      AuthService.resendRegistrationOTPToEmployer(employerCode),
  });
};

export const useVerifyRegistrationOTP = () => {
  return useMutation<BaseResponse<unknown>, Error, VerifyOTPDto>({
    mutationFn: (dto) => AuthService.verifyRegistrationOTP(dto),
  });
};

export const useResendAuthorizedContactVerificationOTP = () => {
  return useMutation<BaseResponse<unknown>, Error, string>({
    mutationFn: (employerCode) =>
      AuthService.resendAuthorizedContactVerificationOTP(employerCode),
  });
};

export const useVerifyAuthorizedContactVerificationOTP = () => {
  return useMutation<BaseResponse<unknown>, Error, VerifyOTPDto>({
    mutationFn: (dto) =>
      AuthService.verifyAuthorizedContactVerificationOTP(dto),
  });
};

export const useCreateAuthorizedContact = () => {
  return useMutation<BaseResponse<unknown>, Error, CreateAuthorizedContactDto>({
    mutationFn: (dto) => AuthService.createAuthorizedContact(dto),
  });
};

